// A request to ADD a vehicle (the car itself is the direct object of add/register):
// "add a vehicle", "add my car", "register a new car", "add another vehicle", "add vehicle".
// Deliberately does NOT match "add oil to my car", "add a service", "add odometer", etc.
const ADD_VEHICLE_INTENT_PATTERN = /\b(add|register)\s+((a|an|my|another|new|the|this)\s+)*(vehicle|car|truck|suv|ride|automobile|jeep|sedan)\b/i;
const ADDITIONAL_VEHICLE_OWNERSHIP_PATTERN = /\b(?:i|we)\s+(?:also\s+)?(?:(?:just|recently)\s+)?(?:have|own|drive|got|bought|purchased)\s+(?:(?:a|an|my|the|this)\s+)?(?:(?:another|second|new|additional|extra)\s+)?(?:(?:vehicle|car|truck|suv|ride|automobile|sedan)\s*(?:,|:|-)?\s*)?/i;
const GENERIC_ADDITIONAL_VEHICLE_PATTERN = /\b(?:i|we)\s+(?:(?:just|recently)\s+)?(?:have|own|drive|got|bought|purchased)\s+(?:(?:a|an|my|the|this)\s+)?(?:another|second|new|additional|extra)\s+(?:vehicle|car|truck|suv|ride|automobile|sedan)\b|\b(?:i|we)\s+also\s+(?:(?:just|recently)\s+)?(?:have|own|drive|got|bought|purchased)\s+(?:(?:a|an|my|the|this)\s+)?(?:vehicle|car|truck|suv|ride|automobile|sedan)\b/i;
const GARAGE_SAVE_ACTION_PATTERN = /\b(?:can\s+you\s+|could\s+you\s+|please\s+)?(?:put|save|add|register)\s+(?:it|this|that|(?:the|my|this|that)\s+(?:vehicle|car|truck|suv|ride|automobile|sedan))\s+(?:in|into|to)\s+(?:my\s+)?garage\b/i;
// A 17-char VIN token - such messages keep the fast VIN path instead of the form.
export const VIN_TOKEN_PATTERN = /\b[A-HJ-NPR-Z0-9]{17}\b/i;

// Words that signal a diagnostic report / question / comparison rather than a plain
// "here's a car to add" - a bare spec that hits any of these is NOT treated as an add.
const NON_ADD_VEHICLE_MARKERS = /\b(won'?t|wont|not\s+start|no\s+start|start|starting|stall|misfire|overheat|leak|smoke|smell|noise|sound|vibrat|shak|rattle|light|warning|check\s*engine|brake|clutch|gear|slip|help|how|what|why|when|where|which|who|is|are|does|do|can|should|would|could|tell|about|reliab|compare|vs|versus|worth|buy|price|cost|mileage|mpg|fuel|economy|recall|part|parts|problem|issue|fix|diagnos|symptom)\b/i;
const NON_ADD_KNOWN_MAKE_CONTEXT = /\b(won'?t|wont|not\s+start|no\s+start|start(?:ing)?|stall|misfire|overheat|leak|smoke|smell|noise|sound|vibrat|shak|rattle|light|warning|check\s*engine|brakes?|clutch|gear|slip|reliab|compare|vs|versus|worth|buy|price|cost|mileage|mpg|fuel|economy|recall|parts?|problem|issue|fix|diagnos|symptom|work\s*list|work\s*plan|service\s*history|service\s*record|maintenance|repair|replace|replaced|changed|logged?)\b/i;

// Common vehicle makes (multi-word first for greedy matching). Used to pull the
// make/model out of a free-text add request so the form opens pre-filled.
const VEHICLE_MAKES = [
    'mercedes-benz', 'mercedes benz', 'land rover', 'range rover', 'rolls-royce', 'rolls royce',
    'alfa romeo', 'aston martin', 'maruti suzuki',
    'toyota', 'honda', 'ford', 'chevrolet', 'chevy', 'nissan', 'hyundai', 'kia', 'bmw', 'mercedes', 'benz',
    'audi', 'volkswagen', 'vw', 'jeep', 'dodge', 'ram', 'gmc', 'subaru', 'mazda', 'lexus', 'acura', 'infiniti',
    'volvo', 'porsche', 'tesla', 'jaguar', 'mitsubishi', 'buick', 'cadillac', 'chrysler', 'lincoln', 'mini', 'fiat',
    'genesis', 'maserati', 'bentley', 'ferrari', 'lamborghini', 'mclaren', 'bugatti', 'suzuki', 'maruti', 'tata',
    'mahindra', 'renault', 'peugeot', 'skoda', 'datsun', 'mg', 'citroen', 'opel', 'seat', 'lotus', 'isuzu', 'scion',
    'saturn', 'pontiac', 'hummer', 'saab', 'byd', 'rivian', 'lucid', 'polestar',
];

const _titleCaseToken = (w) => (/\d/.test(w) ? w : (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w));
const _titleCaseWords = (s) => String(s).split(/\s+/).filter(Boolean).map(_titleCaseToken).join(' ');

const messageHasKnownMake = (text = '') => {
    const lower = ` ${String(text).toLowerCase()} `;
    return VEHICLE_MAKES.some((m) => lower.includes(` ${m} `));
};

// Things you "add" that are NOT vehicles - so "add my oil" / "add a service" never
// get mistaken for a vehicle add.
const NON_VEHICLE_ADD_OBJECTS = /\b(oil|coolant|fluid|fuel|gas|air|service|records?|notes?|documents?|docs?|reminder|mods?|modifications?|parts?|tools?|mileage|odometer|reading|photos?|images?|pictures?|receipts?|invoices?|warranty|insurance|tyres?|tires?|battery|brakes?|filter|wiper|bulb|fuse|belt|plugs?|spark)\b/i;

// Removal / deletion intent - a message like "remove verna" or "delete my 2020 civic"
// must NEVER open the Add Vehicle form, even though it carries a year + known model.
const REMOVE_VEHICLE_MARKERS = /\b(remove|delete|drop|discard|unregister|deregister|de-?list|forget|get\s+rid\s+of|take\s+off|take\s+out)\b/i;

// Common model -> make, so "add my mustang" pre-fills Make too. Not exhaustive - an
// unknown model just leaves Make blank (the user fills it or uses VIN lookup).
const POPULAR_MODEL_TO_MAKE = {
    // Ford
    mustang: 'Ford', f150: 'Ford', f250: 'Ford', f350: 'Ford', focus: 'Ford', fiesta: 'Ford',
    explorer: 'Ford', escape: 'Ford', ranger: 'Ford', bronco: 'Ford', ecosport: 'Ford',
    edge: 'Ford', expedition: 'Ford', fusion: 'Ford', taurus: 'Ford', maverick: 'Ford', transit: 'Ford', endeavour: 'Ford',
    // Dodge
    challenger: 'Dodge', charger: 'Dodge', durango: 'Dodge', journey: 'Dodge', dart: 'Dodge', hornet: 'Dodge', 'grandcaravan': 'Dodge',
    // Chevrolet
    silverado: 'Chevrolet', malibu: 'Chevrolet', equinox: 'Chevrolet', tahoe: 'Chevrolet',
    camaro: 'Chevrolet', corvette: 'Chevrolet', colorado: 'Chevrolet', traverse: 'Chevrolet',
    suburban: 'Chevrolet', impala: 'Chevrolet', cruze: 'Chevrolet', spark: 'Chevrolet', trax: 'Chevrolet', blazer: 'Chevrolet', trailblazer: 'Chevrolet', beat: 'Chevrolet',
    // Toyota
    camry: 'Toyota', corolla: 'Toyota', rav4: 'Toyota', highlander: 'Toyota', tacoma: 'Toyota',
    tundra: 'Toyota', prius: 'Toyota', '4runner': 'Toyota', innova: 'Toyota', fortuner: 'Toyota',
    sienna: 'Toyota', avalon: 'Toyota', sequoia: 'Toyota', supra: 'Toyota', yaris: 'Toyota',
    'landcruiser': 'Toyota', hilux: 'Toyota', glanza: 'Toyota', urbancruiser: 'Toyota', crown: 'Toyota', chr: 'Toyota',
    // Honda
    civic: 'Honda', accord: 'Honda', crv: 'Honda', pilot: 'Honda', odyssey: 'Honda', jazz: 'Honda',
    city: 'Honda', hrv: 'Honda', passport: 'Honda', ridgeline: 'Honda', insight: 'Honda',
    amaze: 'Honda', brv: 'Honda', wrv: 'Honda', elevate: 'Honda',
    // Hyundai
    verna: 'Hyundai', creta: 'Hyundai', i20: 'Hyundai', i10: 'Hyundai', venue: 'Hyundai',
    elantra: 'Hyundai', tucson: 'Hyundai', santafe: 'Hyundai', sonata: 'Hyundai', kona: 'Hyundai',
    palisade: 'Hyundai', accent: 'Hyundai', ioniq: 'Hyundai', veloster: 'Hyundai', exter: 'Hyundai', alcazar: 'Hyundai', aura: 'Hyundai',
    // Kia
    seltos: 'Kia', sonet: 'Kia', sportage: 'Kia', sorento: 'Kia', carnival: 'Kia',
    telluride: 'Kia', forte: 'Kia', soul: 'Kia', stinger: 'Kia', niro: 'Kia', rio: 'Kia', k5: 'Kia', ev6: 'Kia', carens: 'Kia', syros: 'Kia',
    // Nissan
    altima: 'Nissan', sentra: 'Nissan', rogue: 'Nissan', pathfinder: 'Nissan', magnite: 'Nissan',
    murano: 'Nissan', maxima: 'Nissan', versa: 'Nissan', kicks: 'Nissan', frontier: 'Nissan',
    armada: 'Nissan', leaf: 'Nissan', juke: 'Nissan', gtr: 'Nissan',
    // Jeep
    wrangler: 'Jeep', cherokee: 'Jeep', compass: 'Jeep', gladiator: 'Jeep', renegade: 'Jeep',
    'grandcherokee': 'Jeep', wagoneer: 'Jeep', meridian: 'Jeep',
    // Tata / Mahindra / Maruti (India)
    punch: 'Tata', nexon: 'Tata', harrier: 'Tata', safari: 'Tata', altroz: 'Tata', tiago: 'Tata', tigor: 'Tata', curvv: 'Tata',
    thar: 'Mahindra', scorpio: 'Mahindra', xuv700: 'Mahindra', xuv300: 'Mahindra', bolero: 'Mahindra', xuv400: 'Mahindra', 'scorpio-n': 'Mahindra',
    swift: 'Suzuki', baleno: 'Suzuki', brezza: 'Suzuki', dzire: 'Suzuki', ertiga: 'Suzuki', 'grandvitara': 'Suzuki', fronx: 'Suzuki', jimny: 'Suzuki', 'wagonr': 'Suzuki', alto: 'Suzuki',
    // Ram / GMC
    '1500': 'Ram', '2500': 'Ram', '3500': 'Ram',
    sierra: 'GMC', yukon: 'GMC', acadia: 'GMC', terrain: 'GMC', canyon: 'GMC',
    // Subaru
    outback: 'Subaru', forester: 'Subaru', impreza: 'Subaru', crosstrek: 'Subaru', legacy: 'Subaru', ascent: 'Subaru', wrx: 'Subaru', brz: 'Subaru',
    // Mazda
    cx5: 'Mazda', cx30: 'Mazda', cx50: 'Mazda', cx90: 'Mazda', cx9: 'Mazda', cx3: 'Mazda',
    mazda3: 'Mazda', mazda6: 'Mazda', mx5: 'Mazda', miata: 'Mazda',
    // Volkswagen
    golf: 'Volkswagen', jetta: 'Volkswagen', passat: 'Volkswagen', tiguan: 'Volkswagen',
    atlas: 'Volkswagen', taos: 'Volkswagen', id4: 'Volkswagen', virtus: 'Volkswagen', taigun: 'Volkswagen', polo: 'Volkswagen', vento: 'Volkswagen',
    // Tesla
    model3: 'Tesla', models: 'Tesla', modelx: 'Tesla', modely: 'Tesla', cybertruck: 'Tesla',
    // GMC handled above; Audi / BMW / Mercedes (multi-char models kept simple)
    a3: 'Audi', a4: 'Audi', a6: 'Audi', q3: 'Audi', q5: 'Audi', q7: 'Audi', etron: 'Audi',
    x1: 'BMW', x3: 'BMW', x5: 'BMW', x7: 'BMW', m3: 'BMW', m5: 'BMW', i4: 'BMW', ix: 'BMW',
    '3series': 'BMW', '5series': 'BMW', gle: 'Mercedes', glc: 'Mercedes', gla: 'Mercedes', cclass: 'Mercedes', eclass: 'Mercedes', sclass: 'Mercedes',
};

// Look up the make a model belongs to (e.g. "Challenger" -> "Dodge"). Normalizes
// spacing/hyphens so "grand cherokee", "CX-5", "Model 3" all resolve.
export const makeForModel = (model = '') => {
    const k = String(model).toLowerCase().replace(/[\s-]/g, '');
    return POPULAR_MODEL_TO_MAKE[k] || '';
};

const messageHasKnownModel = (text = '') => {
    const lower = ` ${String(text).toLowerCase().replace(/[-]/g, ' ')} `;
    return Object.keys(POPULAR_MODEL_TO_MAKE).some((m) => lower.includes(` ${m} `));
};

const messageHasAddableVehicleSpec = (text = '') => {
    const hasYear = /\b(19\d{2}|20\d{2})\b/.test(text);
    const hasKnownMake = messageHasKnownMake(text);
    const hasKnownModel = messageHasKnownModel(text);
    return (hasYear && (hasKnownMake || hasKnownModel)) || (hasKnownMake && hasKnownModel);
};

// Adding a vehicle is captured through the guided "Add your vehicle" form, not a
// back-and-forth chat. True when the message is an add request, a bare vehicle spec
// (e.g. "tata punch 2025"), or a VIN paste (the form opens pre-filled and looks it up).
export const isAddVehicleFormRequest = (text = '') => {
    const t = String(text).trim();
    if (!t) return false;
    // A removal/deletion request is never an add - e.g. answering "which Verna?" with
    // "remove 2020 verna" must not be misread as adding a "Remove Verna".
    if (REMOVE_VEHICLE_MARKERS.test(t)) return false;
    // 0) A message dominated by a VIN (bare VIN, or "add ... <VIN>") -> open the form
    //    pre-filled with the VIN; the form's Lookup auto-fills year/make/model. A VIN
    //    buried in a longer sentence (e.g. a diagnostic) is left for the normal flow.
    const vinMatch = t.match(VIN_TOKEN_PATTERN);
    if (vinMatch) {
        const remainderWords = t.replace(VIN_TOKEN_PATTERN, ' ').match(/[a-zA-Z]{2,}/g) || [];
        return remainderWords.length <= 3 || /\b(add|register|save)\b/i.test(t);
    }
    // 1) Explicit "add/register a vehicle/car ...".
    if (ADD_VEHICLE_INTENT_PATTERN.test(t)) return true;
    // 1a) Natural language like "I just bought a new car, can you put it in
    // my garage" is an add request even though it contains question words.
    if (
        GARAGE_SAVE_ACTION_PATTERN.test(t) &&
        (messageHasAddableVehicleSpec(t) || ADDITIONAL_VEHICLE_OWNERSHIP_PATTERN.test(t) || GENERIC_ADDITIONAL_VEHICLE_PATTERN.test(t))
    ) return true;
    // 1b) Ownership phrasing like "I also have a 2000 Jeep Cherokee" is an
    // add-vehicle intent, but only when it includes a recognizable vehicle spec.
    if (
        ADDITIONAL_VEHICLE_OWNERSHIP_PATTERN.test(t) &&
        !t.includes('?') &&
        !NON_ADD_VEHICLE_MARKERS.test(t) &&
        (messageHasAddableVehicleSpec(t) || GENERIC_ADDITIONAL_VEHICLE_PATTERN.test(t))
    ) return true;
    // 2) "add/register ... <known make OR known model> ..." (e.g. "add my honda civic",
    // "i want to add my corolla"). A bare model identifies the make too (corolla -> Toyota),
    // so the form can open pre-filled. Keep this narrower than the explicit vehicle/car path
    // above: "add Honda Civic brake pads to my work list" is an add action, not an Add Vehicle
    // action (excluded by the non-vehicle-object / non-add-context guards).
    if (
        /\b(add|register)\b/i.test(t) &&
        (messageHasKnownMake(t) || messageHasKnownModel(t)) &&
        !NON_VEHICLE_ADD_OBJECTS.test(t) &&
        !NON_ADD_KNOWN_MAKE_CONTEXT.test(t)
    ) return true;
    // 2b) "add my mustang" - add/register + determiner + a noun that isn't a known
    //     non-vehicle object (oil/service/...). Short messages only, so a diagnostic like
    //     "add my mustang won't start" (5+ words) isn't swept in.
    if (
        t.split(/\s+/).length <= 4 &&
        /^\s*(add|register)\s+(a|an|my|another|new|the|this)\s+\w+/i.test(t) &&
        !NON_VEHICLE_ADD_OBJECTS.test(t) &&
        !NON_ADD_VEHICLE_MARKERS.test(t)
    ) return true;
    // 3) A bare vehicle spec: a known make + a year, short, with nothing diagnostic /
    //    question-like (so "my toyota won't start" or "is the camry reliable" don't match).
    const words = t.split(/\s+/);
    if (words.length <= 5 && !t.includes('?') && !NON_ADD_VEHICLE_MARKERS.test(t)) {
        if (/\b(19\d{2}|20\d{2})\b/.test(t) && (messageHasKnownMake(t) || messageHasKnownModel(t))) return true;
    }
    return false;
};

// CarFido's previous turn offered to add a VIN it decoded from an uploaded document
// (it did not match a saved vehicle). Detect that and pull out the decoded details
// so the Add Vehicle form can open PRE-FILLED. Returns {vin, year, make, model} or
// null. The form's VIN Lookup auto-fills year/make/model from the VIN on open, so
// the VIN alone is enough; make/model/year are a best-effort bonus.
export const extractDecodedVinPrefill = (assistantText = '') => {
    const text = String(assistantText || '');
    if (!text) return null;
    // Only treat it as a decoded-vehicle "add this?" offer, not just any message
    // that happens to mention a VIN.
    const looksLikeOffer =
        /\bvin\b/i.test(text) && (
            /document vehicle/i.test(text) ||
            /does\s*n['o]t\s*match|doesn'?t match|not\s+match/i.test(text) ||
            /add this vehicle|add it to (?:my )?garage|choose a vehicle|did not attach|didn'?t attach|add the matching vehicle/i.test(text) ||
            /decoded/i.test(text)
        );
    if (!looksLikeOffer) return null;

    // Scope to the "Document vehicle: …" segment (stop at Shop/Total/list) so we don't
    // read a year/make from the "vehicles registered in your account" list that follows.
    const dvSeg = (text.match(/document vehicle:\s*(.*?)(?:\s+shop\b|\s+total\b|\.\s|\bhere are\b|\n|$)/i) || [])[1] || '';

    // Extract the VIN. Prefer the token explicitly labeled "VIN <token>" (accepts the
    // non-standard lengths OCR sometimes produces, e.g. 16 chars), then a strict 17-char
    // VIN anywhere.
    const labeled =
        (dvSeg.match(/\bvin[:\s#]+([A-HJ-NPR-Z0-9]{8,17})\b/i) || [])[1] ||
        (text.match(/\bvin[:\s#]+([A-HJ-NPR-Z0-9]{8,17})\b/i) || [])[1] ||
        (text.match(VIN_TOKEN_PATTERN) || [])[0];
    if (!labeled) return null;
    const vin = labeled.toUpperCase();

    let year = '';
    const yearMatch = dvSeg.match(/\b(19\d{2}|20\d{2})\b/);
    if (yearMatch) year = yearMatch[1];

    let make = '', model = '';
    const desc = dvSeg
        .replace(new RegExp(vin, 'ig'), ' ')       // drop the VIN itself (any length)
        .replace(VIN_TOKEN_PATTERN, ' ')
        .replace(/\bvin[:\s#]*/ig, ' ')
        .replace(/\b(19\d{2}|20\d{2})\b/g, ' ')
        .replace(/\b(shop|total)\b.*$/i, ' ')
        .trim();
    if (desc) {
        const parsed = parseVehicleFromAddRequest(`add ${desc}`);
        make = parsed.make || '';
        model = parsed.model || '';
    }
    return { vin, year, make, model };
};

// A reply that accepts CarFido's decoded-VIN "add this vehicle?" offer. Deliberately
// liberal because it is ONLY used when such an offer immediately preceded — e.g.
// "save vehicle to Garage", "save the decoded VIN to my garage", "add this vehicle",
// "add it to my garage", "yes", "save it". A removal/decline is excluded.
export const isSaveDecodedVehicleReply = (text = '') => {
    const t = String(text).trim().toLowerCase();
    if (!t) return false;
    if (REMOVE_VEHICLE_MARKERS.test(t)) return false;
    if (/\bdo\s*n['o]?t\s+save\b|\bdon'?t save\b|\bnot save\b|\bcancel\b|\bchoose\s+(a\s+)?vehicle\b/.test(t)) return false;
    // Save/add + a vehicle-ish object (vehicle/car/vin/it/this/decoded/garage).
    if (/\b(save|add|register)\b/.test(t) && /\b(vehicle|car|truck|suv|vin|it|this|decoded|garage)\b/.test(t)) return true;
    // Bare affirmative.
    if (/^(yes|yeah|yep|yup|sure|ok|okay|please\s*do|go\s*ahead|do\s*it|add\s*it|save\s*it|yes\s*please)\b/.test(t)) return true;
    return false;
};

// Best-effort parse of year/make/model/odometer from a free-text add-vehicle request
// like "add a vehicle Hyundai Verna 2023" so the form opens pre-filled with what was given.
export const parseVehicleFromAddRequest = (text = '') => {
    let s = ` ${String(text)} `;
    // Drop the intent phrase and common filler words.
    s = s.replace(/\b(please|can you|could you|i\s*want\s*to|i'?d\s*like\s*to|help\s*me)\b/gi, ' ');
    s = s.replace(/\b(?:can|could|would|will|may)\s+(?:i|we|you)\b\s*(?:please\s+)?/gi, ' ');
    s = s.replace(/\b(?:is|would)\s+it\s+(?:be\s+)?possible\s+to\b/gi, ' ');
    s = s.replace(/\b(add|register)\s+((a|an|my|another|other|new|the|this|next|last|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|\d+(?:st|nd|rd|th))\s+)*(vehicle|car|truck|suv|ride|automobile|jeep|sedan)\b/i, ' ');
    s = s.replace(ADDITIONAL_VEHICLE_OWNERSHIP_PATTERN, ' ');
    s = s.replace(GARAGE_SAVE_ACTION_PATTERN, ' ');
    s = s.replace(/^\s*(a|an|my|the|this)\s+/i, ' ');
    // Also strip a leading "add/register [a|my|new|...]" with NO vehicle keyword, so
    // "add my mustang" -> "mustang" (not "add my mustang" leaking into the model).
    s = s.replace(/^\s*(add|register)\s+((a|an|my|another|other|new|the|this|next|last|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|\d+(?:st|nd|rd|th))\s+)*/i, ' ');
    s = s.replace(/\bto\s+(my\s+)?garage\b/gi, ' ');
    // Strip conversational leaders so a spoken phrase like "It's a 2026 Mustang" or
    // "the car is a Camry" doesn't leak "it's a" / "the car is" into the model.
    s = s.replace(/\b(it'?s|it\s+is|this\s+is|that'?s|that\s+is|here'?s|here\s+is)\s+(a|an|the)?\s*/gi, ' ');
    s = s.replace(/\b(the|my|our)\s+(car|vehicle|truck|suv|ride)\s+is\s+(a|an|the)?\s*/gi, ' ');
    s = s.replace(/\b(it'?s|called|named)\s+/gi, ' ');

    // Year (1900-2099).
    let year = '';
    const yearMatch = s.match(/\b(19\d{2}|20\d{2})\b/);
    if (yearMatch) { year = yearMatch[1]; s = s.replace(yearMatch[0], ' '); }

    // Odometer ("45,000 miles", "45000 km", "45k miles").
    let odometerReading = '';
    const odoMatch = s.match(/\b(\d[\d,]{2,7}|\d+\s*k)\s*(miles|mile|mi|kms?|kilometers?)\b/i);
    if (odoMatch) {
        let n = odoMatch[1].replace(/[,\s]/g, '').toLowerCase();
        if (n.endsWith('k')) n = String(parseInt(n, 10) * 1000);
        odometerReading = n;
        s = s.replace(odoMatch[0], ' ');
    }

    // Make (prefer a known multi/single-word make); the remaining tokens are the model.
    const cleaned = s.replace(/[^\w\s-]/g, ' ').replace(/\s+/g, ' ').trim();
    let make = '', model = '';
    // Residual filler that must never end up in the model (e.g. from "it's a" -> "it s a").
    const _MODEL_STOPWORDS = new Set([
        'it', 'its', 'is', 'a', 'an', 'the', 'this', 'that', 'here', 'my', 'our', 'we',
        'i', 'have', 'has', 'had', 'own', 'owns', 'got', 'get', 'new', 'car', 'cars',
        'vehicle', 'vehicles', 'truck', 'suv', 'ride', 's', 'be', 'called', 'named',
        'name', 'of', 'mine', 'just', 'now', 'add', 'register', 'save', 'put', 'can',
        'could', 'would', 'will', 'may', 'might', 'should', 'do', 'does', 'did', 'please',
        'possible', 'am', 'are', 'able', 'allowed', 'let', 'me', 'you', 'to', 'for',
        'how', 'what', 'why', 'when', 'where', 'which', 'who', 'whom', 'if', 'dash',
        // Ordinals referring to WHICH vehicle ("add the second vehicle") — not a model.
        'another', 'other', 'next', 'last', 'first', 'second', 'third', 'fourth', 'fifth',
        'sixth', 'seventh', 'eighth', 'ninth', 'tenth',
        '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th',
    ]);
    const _dropFiller = (arr) => arr.filter((tk) => tk && !_MODEL_STOPWORDS.has(tk.toLowerCase()));
    if (cleaned) {
        const tokens = cleaned.split(' ').filter(Boolean);
        const lowerTokens = tokens.map((t) => t.toLowerCase());
        let makeStart = -1, makeLen = 0;
        for (const m of VEHICLE_MAKES) {
            const parts = m.split(/[-\s]+/);
            for (let i = 0; i + parts.length <= lowerTokens.length; i++) {
                if (parts.every((p, j) => lowerTokens[i + j] === p)) { makeStart = i; makeLen = parts.length; break; }
            }
            if (makeStart >= 0) break;
        }
        if (makeStart >= 0) {
            make = tokens.slice(makeStart, makeStart + makeLen).join(' ');
            model = _dropFiller([...tokens.slice(0, makeStart), ...tokens.slice(makeStart + makeLen)]).join(' ');
        } else {
            // No recognized make. Drop conversational filler, then prefer a recognized
            // MODEL token — which also identifies the make (e.g. "mustang" -> Ford).
            const content = _dropFiller(tokens);
            let knownTok = '';
            for (const tk of content) {
                if (POPULAR_MODEL_TO_MAKE[tk.toLowerCase().replace(/[\s-]/g, '')]) { knownTok = tk; break; }
            }
            if (knownTok) {
                model = knownTok;
                make = POPULAR_MODEL_TO_MAKE[knownTok.toLowerCase().replace(/[\s-]/g, '')];
            } else {
                model = content.join(' ');
                const known = POPULAR_MODEL_TO_MAKE[model.toLowerCase().replace(/[\s-]/g, '')];
                if (known) make = known;
            }
        }
    }

    return {
        year,
        make: make ? _titleCaseWords(make) : '',
        model: model ? _titleCaseWords(model) : '',
        odometerReading,
    };
};
